import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { Save, CheckCircle, Info, PartyPopper, AlertTriangle, Megaphone } from 'lucide-react';
import { Spinner } from '../components/Spinner';
import styles from './SettingsPage.module.css';

type AnnouncementType = 'info' | 'success' | 'warning' | 'urgent';

interface AnnouncementForm {
  message: string;
  isEnabled: boolean;
  type: AnnouncementType;
  linkUrl: string;
  linkText: string;
  startsAt: string;
  endsAt: string;
  dismissible: boolean;
}

const initialForm: AnnouncementForm = {
  message: '',
  isEnabled: false,
  type: 'info',
  linkUrl: '',
  linkText: '',
  startsAt: '',
  endsAt: '',
  dismissible: true,
};

const TYPE_OPTIONS: { value: AnnouncementType; label: string; icon: React.ReactNode; colors: { bg: string; text: string } }[] = [
  { value: 'info', label: 'Info (blue)', icon: <Info size={16} />, colors: { bg: '#2563eb', text: '#ffffff' } },
  { value: 'success', label: 'Success (green)', icon: <PartyPopper size={16} />, colors: { bg: '#059669', text: '#ffffff' } },
  { value: 'warning', label: 'Warning (amber)', icon: <AlertTriangle size={16} />, colors: { bg: '#d97706', text: '#ffffff' } },
  { value: 'urgent', label: 'Urgent (red)', icon: <Megaphone size={16} />, colors: { bg: '#dc2626', text: '#ffffff' } },
];

function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function getStatus(form: AnnouncementForm): { label: string; style: React.CSSProperties } {
  if (!form.isEnabled || !form.message.trim()) {
    return { label: 'Disabled', style: { background: '#f3f4f6', color: '#6b7280' } };
  }
  const now = new Date();
  if (form.startsAt && now < new Date(form.startsAt)) {
    return { label: 'Scheduled', style: { background: '#dbeafe', color: '#1e40af' } };
  }
  if (form.endsAt && now > new Date(form.endsAt)) {
    return { label: 'Ended', style: { background: '#fee2e2', color: '#991b1b' } };
  }
  return { label: 'Active now', style: { background: '#d1fae5', color: '#065f46' } };
}

export function AnnouncementPage() {
  const { token } = useAuth();
  const [formData, setFormData] = useState<AnnouncementForm>(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const fetchAnnouncement = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/announcement`);
      const data = await response.json();
      if (data.announcement) {
        setFormData({
          message: data.announcement.message || '',
          isEnabled: !!data.announcement.isEnabled,
          type: data.announcement.type || 'info',
          linkUrl: data.announcement.linkUrl || '',
          linkText: data.announcement.linkText || '',
          startsAt: toDatetimeLocal(data.announcement.startsAt),
          endsAt: toDatetimeLocal(data.announcement.endsAt),
          dismissible: data.announcement.dismissible !== false,
        });
      }
    } catch (err) {
      console.error('Error fetching announcement:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncement();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setSaved(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      const response = await fetch(`${API_BASE_URL}/api/announcement`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: formData.message,
          isEnabled: formData.isEnabled,
          type: formData.type,
          linkUrl: formData.linkUrl,
          linkText: formData.linkText,
          startsAt: formData.startsAt ? new Date(formData.startsAt).toISOString() : null,
          endsAt: formData.endsAt ? new Date(formData.endsAt).toISOString() : null,
          dismissible: formData.dismissible,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to save announcement');
      }
      setSaved(true);
      fetchAnnouncement();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner label="Loading announcement settings..." size="lg" />;
  }

  const status = getStatus(formData);
  const activeType = TYPE_OPTIONS.find((t) => t.value === formData.type) || TYPE_OPTIONS[0];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Announcement</h1>
        <p>
          Controls the sitewide announcement banner. Use it for anything
          important — closures, new product drops, delivery delays. Changes
          go live on the storefront immediately — no redeploy needed.
        </p>
      </header>

      <div className={styles.card}>
        {error && <div className={styles.error}>{error}</div>}
        {saved && (
          <div className={styles.success}>
            <CheckCircle size={18} />
            Saved — live on the storefront now.
          </div>
        )}

        <div
          style={{
            display: 'inline-flex',
            padding: '0.375rem 0.875rem',
            borderRadius: 9999,
            fontSize: '0.8125rem',
            fontWeight: 600,
            marginBottom: '1.25rem',
            ...status.style,
          }}
        >
          {status.label}
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.toggleRow}>
            <label htmlFor="isEnabled">Announcement Enabled</label>
            <label className={styles.switch}>
              <input
                type="checkbox"
                id="isEnabled"
                name="isEnabled"
                checked={formData.isEnabled}
                onChange={handleChange}
              />
              <span className={styles.switchSlider} />
            </label>
          </div>

          <div className={styles.formGroup}>
            <label>Message</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="e.g. We're closed on Aug 30 for Eid — orders resume Aug 31!"
              rows={3}
            />
            <span className={styles.hint}>
              Shown in the banner at the top of every storefront page.
            </span>
          </div>

          <div className={styles.formGroup}>
            <label>Banner Style</label>
            <select name="type" value={formData.type} onChange={handleChange}>
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Link URL (optional)</label>
              <input
                type="text"
                name="linkUrl"
                value={formData.linkUrl}
                onChange={handleChange}
                placeholder="/products/workshops"
              />
              <span className={styles.hint}>
                A page on the site, e.g. /products/kits
              </span>
            </div>
            <div className={styles.formGroup}>
              <label>Link Text (optional)</label>
              <input
                type="text"
                name="linkText"
                value={formData.linkText}
                onChange={handleChange}
                placeholder="Learn More"
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Starts At (optional)</label>
              <input
                type="datetime-local"
                name="startsAt"
                value={formData.startsAt}
                onChange={handleChange}
              />
              <span className={styles.hint}>Leave blank to show as soon as enabled.</span>
            </div>
            <div className={styles.formGroup}>
              <label>Ends At (optional)</label>
              <input
                type="datetime-local"
                name="endsAt"
                value={formData.endsAt}
                onChange={handleChange}
              />
              <span className={styles.hint}>Leave blank to show until turned off.</span>
            </div>
          </div>

          <div className={styles.toggleRow}>
            <label htmlFor="dismissible">Let users close the banner</label>
            <label className={styles.switch}>
              <input
                type="checkbox"
                id="dismissible"
                name="dismissible"
                checked={formData.dismissible}
                onChange={handleChange}
              />
              <span className={styles.switchSlider} />
            </label>
          </div>

          {formData.message.trim() && (
            <div className={styles.formGroup}>
              <label>Preview</label>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  textAlign: 'center',
                  fontWeight: 600,
                  background: activeType.colors.bg,
                  color: activeType.colors.text,
                }}
              >
                {activeType.icon}
                <span>{formData.message}</span>
                {formData.linkText && (
                  <span
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: 9999,
                      background: 'rgba(255,255,255,0.22)',
                      fontSize: '0.8125rem',
                    }}
                  >
                    {formData.linkText}
                  </span>
                )}
              </div>
            </div>
          )}

          <button type="submit" className={styles.submitBtn} disabled={saving}>
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Announcement'}
          </button>
        </form>
      </div>
    </div>
  );
}
